import { trackLegacyEvent } from "@analytics";
import {
  Body,
  Box,
  BulletList,
  CloudImage,
  Divider,
  Heading,
  Stack,
} from "@biom3/react";
import { SliceBox } from "@cms/components";
import { useSliceContext } from "@cms/context";
import { getWebLink } from "@cms/utils";
import type { Content } from "@prismicio/client";
import { PrismicRichText, type SliceComponentProps } from "@prismicio/react";
import { PlayButton } from "@ui-kit/CTAButtons/PlayButton";
import { WishlistButton } from "@ui-kit/CTAButtons/WishlistButton";
/**
 * Props for `InformationSection`.
 */
export type InformationSectionProps =
  SliceComponentProps<Content.InformationSectionSlice>;

/**
 * Component for "InformationSection" Slices.
 */
const InformationSection = ({
  slice,
  index,
}: InformationSectionProps): JSX.Element => {
  const { wishlistProps } = useSliceContext();

  const playLink = getWebLink(slice.primary.primary_cta)?.url || "#";

  const handlePlayButtonClick = (link: string) => {
    trackLegacyEvent({
      screen: "Details",
      userJourney: "Games",
      control: "PlayNowClicked",
      controlType: "Button",
      extras: {
        website: link,
      },
    });
  };

  return (
    <SliceBox slice={slice} index={index}>
      <Stack
        sx={{
          minh: { default: "1070px", large: "1070px" },
          pos: "relative",
          mx: "-24px",
          pl: { default: "base.spacing.x8", xLarge: "base.spacing.x20" },
          pr: {
            default: "base.spacing.x8",
            xLarge: "290px",
          },
          pt: "base.spacing.x21",
        }}
        alignItems="flex-start"
      >
        {slice.primary.title && slice.primary.description.length ? (
          <Stack
            direction={{ default: "column", xLarge: "row" }}
            sx={{
              w: { default: "100%" },
              gap: { default: "0px", xLarge: "base.spacing.x9" },
              zIndex: 2,
            }}
          >
            <Divider
              sx={{
                pos: "absolute",
                top: "0px",
                zIndex: 1,
                left: "40px",
                right: "40px",
                w: "calc(100% - 80px)",
              }}
            />

            <Box
              sx={{
                w: { default: "100%", xLarge: "41%" },
                pt: "base.spacing.x4",
              }}
            >
              <Heading size="large" weight="bold">
                {slice.primary.title}
              </Heading>
            </Box>
            <Box
              sx={{
                w: { default: "100%", xLarge: "59%" },
                pt: "base.spacing.x5",
              }}
            >
              <Body
                sx={{
                  fontSize: "base.text.body.medium.regular.fontSize",
                  color: "base.color.text.body.secondary",
                }}
              >
                <PrismicRichText
                  field={slice.primary.description}
                  components={{
                    list: ({ key, ...props }) => (
                      <BulletList
                        key={key}
                        sx={{
                          "& > ul": {
                            gap: "base.spacing.x2",
                          },
                        }}
                        {...props}
                      />
                    ),
                    listItem: ({ key, ...props }) => (
                      <BulletList.Item
                        key={key}
                        {...props}
                        sx={{
                          color: "base.color.text.body.secondary",
                        }}
                      />
                    ),
                    paragraph: ({ key, ...props }) => (
                      <Body key={key} {...props} />
                    ),
                  }}
                />
              </Body>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                sx={{
                  pt: "base.spacing.x10",
                  gap: "base.spacing.x4",
                }}
              >
                <PlayButton
                  playLink={playLink}
                  analyticsEvent={handlePlayButtonClick}
                  buttonSize="large"
                />
                <WishlistButton
                  buttonSize="large"
                  trackGameDetailsEventIdentifier="InformationSection"
                  wishlistProps={wishlistProps}
                />
              </Stack>
            </Box>
          </Stack>
        ) : null}

        {slice.primary.overlay_image.url ? (
          <CloudImage
            testId="heroImageOverlay"
            imageUrl={slice.primary.overlay_image.url}
            relativeImageSizeInLayout="860px"
            responsiveSizes={[
              512, 640, 720, 860, 1024, 1280, 1440, 1600, 1920, 2048, 2160,
            ]}
            sx={{
              // @TODO: more responsive work needed here
              d: { default: "none", large: "block" },
              pos: "absolute",
              w: {
                default: "860px",
                large: "700px",
                xLarge: "700px",
                xxLarge: "860px",
              },
              bottom: "0px",
              right: "0px",
            }}
          />
        ) : null}
      </Stack>
    </SliceBox>
  );
};

export default InformationSection;
